<template>
  <v-container class="layout--step-message">
    <h2 class="mb-4">
      {{ $t('step_message.history') }}
      <a href="https://blog.zwiz.app/audience-notification-history/" target="blank">
        <i style="font-size: 22px; color: #ff706b" class="fas fa-question-circle"> </i>
      </a>
    </h2>

    <div class="filter-action__wraper">
      <v-btn
        v-for="(action, index) in actionTypes"
        :key="`action-${index}`"
        class="ma-0"
        color="primary"
        :outline="!action.active"
        small
        @click="activeActionType(action)"
      >
        {{ action.text }}
      </v-btn>
    </div>

    <date-filter
      v-model="selectedDate"
      :default-filter="'today'"
      :items-hide="['this_year', 'last_365_day']"
      @changeDate="onDateChanged"
    ></date-filter>

    <v-layout row wrap>
      <v-flex xs12 class="d-flex justify-space-between align-center">
        <custom-paginate
          v-model="pageConfig.page"
          class="my-4"
          :has-next="pageConfig.pageCount >= pageConfig.page"
          :has-prev="pageConfig.pageCount > 1 && pageConfig.page <= pageConfig.pageCount"
          :pagelength="pageConfig.pageCount || 1"
          :loading="isLoading"
          style="max-width: 50%"
          @onChangePage="onClickPageinate"
        >
        </custom-paginate>
        <div
          style="display: flex; column-gap: 2px; align-items: baseline; justify-content: flex-end"
        >
          <span class="caption mr-2">
            {{ $t('Total') }}
          </span>
          <h2>{{ pageConfig.total }}</h2>
        </div>
      </v-flex>
      <v-flex xs12>
        <v-data-table :headers="headers" :items="historyItems" class="elevation-1" hide-actions>
          <template #items="{ item }">
            <td>{{ item.submit_date }}</td>
            <td>{{ item.action_type ? $t(`step_message.${item.action_type}`) : '-' }}</td>
            <td>{{ item.audience_name || '-' }}</td>
            <td class="text-xs-center">{{ item.total_user || 0 }}</td>
            <!-- <td class="text-xs-center">{{ item.platform_total_user || 0 }}</td> -->
            <td class="text-xs-center">
              <div
                v-if="item.platform_channel"
                class="d-inline-block icon px-1"
                style="max-height: 20px; border-radius: 2px !important"
                :style="{
                  'background-color': item.platform_channel === 'line' ? '#06b901' : '#3c5a99'
                }"
              >
                <i :class="`fab white--text fa-${item.platform_channel}`"></i>
              </div>
              <div v-else>-</div>
            </td>
            <td class="text-xs-center">
              <v-icon
                v-if="
                  item.action_type &&
                  item.action_type === 'add' &&
                  item.platform_channel &&
                  item.platform_channel === 'line'
                "
                class="primary--text"
                @click="openDetail(item)"
                >search</v-icon
              >
            </td>
            <td>{{ getCreatedBy(item.created_by) || '-' }}</td>
          </template>
        </v-data-table>
      </v-flex>
      <v-flex xs12>
        <custom-paginate
          v-model="pageConfig.page"
          class="my-4"
          :has-next="pageConfig.pageCount >= pageConfig.page"
          :has-prev="pageConfig.pageCount > 1 && pageConfig.page <= pageConfig.pageCount"
          :pagelength="pageConfig.pageCount || 1"
          :loading="isLoading"
          @onChangePage="onClickPageinate"
        >
        </custom-paginate>
      </v-flex>
    </v-layout>

    <v-dialog v-model="showModalNumber" max-width="400px">
      <v-card>
        <v-card-text>
          <v-layout row wrap class="px-4 py-2 mx-auto" style="max-width: 320px">
            <v-flex x12 class="text-xs-center">
              <h2 class="black--text">{{ $t('step_message.number_of_members') }}</h2>
              <h1 class="mt-4" style="font-size: 54px">{{ numberOfMembers }}</h1>
              <div class="caption text-xs-left mt-4">
                <div class="mb-2" v-text="$t('step_message.history_desc_1')"></div>
                <br />
                <div v-text="$t('step_message.history_desc_2')"></div>
              </div>
            </v-flex>
            <v-flex xs12 class="text-xs-center mt-4">
              <v-btn depressed color="primary" @click="showModalNumber = false">
                {{ $t('close') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'

import DateFilter from '../analytics/DateFilter.vue'
import CustomPaginate from '../../elements/CustomPaginate.vue'
import audienceApi from '@/api/audience'

export default {
  components: { DateFilter, CustomPaginate },
  data: () => ({
    isLoading: false,
    selectedDate: {
      start: dayjs().format('YYYY-MM-DD'),
      end: dayjs().format('YYYY-MM-DD')
    },
    historyItems: [],
    pageConfig: {
      page: 1,
      limit: 10,
      total: 0,
      pageCount: 1,
      hasNext: false,
      hasPrev: false
    },
    search: '',
    image: {
      line: '/static/img/assets/_global/line.svg'
    },
    showModalNumber: false,
    numberOfMembers: 0,
    actionTypeActive: 'all'
  }),
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot',
      currentLang: 'currentLang'
    }),
    headers() {
      return [
        {
          text: this.$t('step_message.data_submission_date'),
          value: 'created_at',
          sortable: false
        },
        { text: this.$t('step_message.action'), value: 'action', sortable: false },
        { text: this.$t('step_message.target_group'), value: 'audience_name', sortable: false },
        {
          text: this.$t('step_message.number_of_members_on_zwiz'),
          align: 'center',
          value: 'total_user',
          sortable: false
        },
        // {
        //   text: this.$t('step_message.number_of_members_on_the_platform'),
        //   align: 'center',
        //   value: 'platform_total_user',
        //   sortable: false,
        // },
        {
          text: this.$t('step_message.channel'),
          value: 'platform_channel',
          align: 'center',
          sortable: false
        },
        {
          text: this.$t('step_message.detail'),
          value: 'detail',
          align: 'center',
          sortable: false
        },
        {
          text: this.$t('step_message.data_submission_method'),
          value: 'created_by',
          sortable: false
        }
      ]
    },
    actionTypes() {
      return [
        {
          text: this.$t('step_message.all'),
          value: 'all',
          active: this.actionTypeActive === 'all'
        },
        {
          text: this.$t('step_message.add'),
          value: 'add',
          active: this.actionTypeActive === 'add'
        },
        {
          text: this.$t('step_message.remove'),
          value: 'remove',
          active: this.actionTypeActive === 'remove'
        }
      ]
    }
  },
  created() {
    this.fetchHistory()
  },
  methods: {
    async fetchHistory() {
      try {
        let start = this.selectedDate.start
        let end = this.selectedDate.end

        if (start === end) {
          start = start + ' 00:00'
          end = end + ' 23:59'
        }

        await this.$store.dispatch('loading')

        const response = await audienceApi.getAudienceUploadHistories({
          page_id: this.selectedBot.pageid,
          limit: this.pageConfig.limit,
          offset: (this.pageConfig.page - 1) * this.pageConfig.limit,
          start_date: start,
          end_date: end,
          action_type: this.actionTypeActive === 'all' ? undefined : this.actionTypeActive
          // name: this.search,
        })
        if (response.status === 200) {
          const total = response.data.result.total_history || 0
          this.historyItems = response.data.result.history.map((item) => {
            item.submit_date = item.created_at
              ? dayjs(item.created_at).format('DD/MM/YYYY HH:mm')
              : ''
            return item
          })
          this.pageConfig.total = total
          this.pageConfig.pageCount = Math.ceil(total / this.pageConfig.limit)
        }
      } catch (error) {
        this.historyItems = []
        this.pageConfig.page = 1
        this.pageConfig.pageLimit = 10
        this.pageConfig.total = 0
        this.pageConfig.pageCount = 1
      } finally {
        await this.$store.dispatch('loading')
      }
    },
    async onDateChanged() {
      this.historyItems = []
      this.pageConfig.page = 1
      this.pageConfig.pageLimit = 10
      this.pageConfig.total = 0
      this.pageConfig.pageCount = 1
      await this.fetchHistory()
    },
    async onClickPageinate(value) {
      if (value) {
        this.historyItems = []
        this.pageConfig.page = value.newpage
        // this.pageConfig.pageLimit = 10
        this.pageConfig.total = 0
        // this.pageConfig.pageCount = 1
        await this.fetchHistory()
      }
    },
    async openDetail(item) {
      try {
        await this.$store.dispatch('loading')
        const response = await audienceApi.getLineAudienceData({
          page_id: this.selectedBot.pageid,
          audience_group_id: item.audience_id
        })
        if (response.status === 200) {
          this.showModalNumber = true
          this.numberOfMembers =
            response.result && response.result.audienceGroup
              ? response.result.audienceGroup.audienceCount
              : 0
        } else {
          await this.$swal.fire({
            type: 'error',
            title: this.$t('Unsuccessful'),
            text: response.data.message || ''
          })
        }
      } catch (error) {
        await this.$swal.fire({
          type: 'error',
          title: this.$t('Unsuccessful'),
          text: error.message || ''
        })
      } finally {
        await this.$store.dispatch('loading')
      }
    },
    async activeActionType(action) {
      this.actionTypeActive = action.value
      this.historyItems = []
      this.pageConfig.page = 1
      this.pageConfig.pageLimit = 10
      this.pageConfig.total = 0
      this.pageConfig.pageCount = 1
      await this.fetchHistory()
    },
    getCreatedBy(createdBy) {
      if (createdBy === 'schedule' || createdBy === 'system') {
        return 'System'
      } else {
        return createdBy
      }
    }
  }
}
</script>

<style scoped>
.layout--step-message {
  min-height: calc(100vh - 64px);
}
</style>

<style lang="scss" scoped>
.filter-action__wraper {
  display: flex;
  align-items: center;
  column-gap: 8px;
  margin-bottom: 16px;
}
</style>
>



// WEBPACK FOOTER //
// src/components/view/stepmessage/AudienceHistory.vue